// theme.js — Erscheinungsbild zur Laufzeit: Modus, Kontrast, eigene Leitfarbe (Dynamic Color).
// Die Farbbibliothek wird nur geladen, wenn eine eigene Leitfarbe gesetzt wird.
import { persist } from '../../shared/store.js';

export const theme = persist('vc-theme', { seed: null, mode: 'system', contrast: 'standard', motion: 'system' });
const root = () => document.documentElement;
let mq = null, mqHandler = null;

function resolvedMode(mode) {
  if (mode === 'light' || mode === 'dark') return mode;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyMode(mode) {
  root().setAttribute('data-theme', resolvedMode(mode));
  if (mq && mqHandler) mq.removeEventListener('change', mqHandler);
  mq = mqHandler = null;
  if (mode !== 'light' && mode !== 'dark' && window.matchMedia) {
    mq = window.matchMedia('(prefers-color-scheme: dark)');
    mqHandler = () => root().setAttribute('data-theme', resolvedMode('system'));
    mq.addEventListener('change', mqHandler);
  }
}

function applyMotion(m) {
  if (m === 'reduced') root().setAttribute('data-motion', 'reduced'); else root().removeAttribute('data-motion');
}

function applyContrast(c) {
  if (c === 'medium' || c === 'high') root().setAttribute('data-contrast', c);
  else root().removeAttribute('data-contrast');
}

/** CSS für eine eigene Leitfarbe (hell + dunkel, aktueller Kontrast), Kopfzeile = Cache-Schlüssel für boot.js. */
export async function seedCss(seed, contrast = 'standard') {
  const { schemeRoles, rolesToCss, CONTRAST_LEVELS } = await import('./scheme.js');
  const level = CONTRAST_LEVELS[contrast] || 0;
  const light = rolesToCss(schemeRoles(seed, { dark: false, contrast: level }));
  const dark = rolesToCss(schemeRoles(seed, { dark: true, contrast: level }));
  return `/*${seed.toUpperCase()}|${contrast}*/ :root[data-seed]:not([data-theme="dark"]) { ${light} } :root[data-seed][data-theme="dark"] { ${dark} }`;
}

async function applySeed(seed, contrast) {
  const old = document.getElementById('vc-seed');
  if (!seed) {
    if (old) old.remove();
    root().removeAttribute('data-seed');
    try { localStorage.removeItem('vc-theme-css'); } catch {}
    return;
  }
  const css = await seedCss(seed, contrast);
  const s = old || Object.assign(document.createElement('style'), { id: 'vc-seed' });
  s.textContent = css;
  if (!old) document.head.appendChild(s);
  root().setAttribute('data-seed', seed);
  try { localStorage.setItem('vc-theme-css', css); } catch {}
}

/** Teilweise setzen: setTheme({ mode: 'dark' }). Speichert lokal; onSave(t) z. B. für PUT /api/me. */
export async function setTheme(patch, { onSave } = {}) {
  const next = { ...theme.peek(), ...patch };
  theme.value = next;
  applyMode(next.mode);
  applyContrast(next.contrast);
  applyMotion(next.motion);
  await applySeed(next.seed, next.contrast);
  if (onSave) onSave(next);
  return next;
}

/** Nach dem Laden: Servereinstellung (preferences.theme) übernehmen, falls sie abweicht. */
export function syncFromServer(serverTheme) {
  if (!serverTheme || typeof serverTheme !== 'object') return;
  const cur = theme.peek();
  if (JSON.stringify({ ...cur, ...serverTheme }) !== JSON.stringify(cur)) setTheme(serverTheme);
}

export function initTheme() { applyMode(theme.peek().mode); }
